import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import {
  Wallet, Plus, Pencil, Trash2, X, Eye, EyeOff, QrCode, Landmark, Coins, MoreHorizontal, Upload, Image as ImageIcon
} from 'lucide-react';

const TYPE_META = {
  upi: { label: 'UPI', icon: Wallet, color: '#3b82f6' },
  bank: { label: 'Bank Transfer', icon: Landmark, color: '#22c55e' },
  usdt: { label: 'USDT / Crypto', icon: Coins, color: 'var(--primary)' },
  other: { label: 'Other', icon: MoreHorizontal, color: '#a78bfa' },
};

const EMPTY_FORM = {
  type: 'upi', label: '', isActive: true, order: 0,
  upiId: '', accountHolderName: '', accountNumber: '', ifscCode: '', bankName: '',
  usdtAddress: '', usdtNetwork: '', otherDetails: '', qrImageUrl: '', instructions: ''
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function AdminWallet() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const qrInputRef = useRef(null);

  const fetchWallets = useCallback(() => {
    setLoading(true);
    api.get('/wallet/all').then(r => setWallets(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchWallets(); }, [fetchWallets]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (w) => {
    setEditing(w);
    setForm({
      type: w.type, label: w.label, isActive: w.isActive, order: w.order || 0,
      upiId: w.upiId || '', accountHolderName: w.accountHolderName || '', accountNumber: w.accountNumber || '',
      ifscCode: w.ifscCode || '', bankName: w.bankName || '', usdtAddress: w.usdtAddress || '',
      usdtNetwork: w.usdtNetwork || '', otherDetails: w.otherDetails || '', qrImageUrl: w.qrImageUrl || '',
      instructions: w.instructions || ''
    });
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleQrFile = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error('QR image must be under 3MB');
    const base64 = await fileToBase64(file);
    setForm(f => ({ ...f, qrImageUrl: base64 }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error('Label is required');
    if (form.type === 'upi' && !form.upiId.trim()) return toast.error('UPI ID is required');
    if (form.type === 'bank' && (!form.accountNumber.trim() || !form.ifscCode.trim())) return toast.error('Account number and IFSC code are required');
    if (form.type === 'usdt' && !form.usdtAddress.trim()) return toast.error('Wallet address is required');

    setSubmitting(true);
    try {
      if (editing) {
        const res = await api.put(`/wallet/${editing._id}`, form);
        toast.success(res.data.message);
      } else {
        const res = await api.post('/wallet', form);
        toast.success(res.data.message);
      }
      closeModal();
      fetchWallets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save wallet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (w) => {
    try {
      const res = await api.put(`/wallet/${w._id}/toggle`);
      toast.success(res.data.message);
      fetchWallets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update wallet');
    }
  };

  const handleDelete = async (w) => {
    if (!window.confirm(`Delete "${w.label}"? Users will no longer see this as a payment option.`)) return;
    try {
      const res = await api.delete(`/wallet/${w._id}`);
      toast.success(res.data.message);
      fetchWallets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete wallet');
    }
  };

  const renderDetail = (w) => {
    if (w.type === 'upi') return <span>{w.upiId}</span>;
    if (w.type === 'bank') return <span>{w.accountHolderName ? `${w.accountHolderName} — ` : ''}{w.accountNumber} ({w.ifscCode})</span>;
    if (w.type === 'usdt') return <span>{w.usdtAddress} {w.usdtNetwork ? `(${w.usdtNetwork})` : ''}</span>;
    return <span>{w.otherDetails || '—'}</span>;
  };

  return (
    <DashboardLayout>
      <h1 className="page-title">Wallet Management</h1>
      <p className="page-subtitle">Set up the UPI / Bank / USDT wallets users must pay into when purchasing a package</p>

      <div className="alert alert-info" style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <Wallet size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>Wallets you mark <strong>Active</strong> here appear on the user's Wallet page and on the package purchase form, so users know exactly where to send payment. After paying, users upload a screenshot which you can review in Package Management.</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary btn-sm" onClick={openAdd} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16} /> Add Wallet
        </button>
      </div>

      <div className="card">
        {loading ? <div className="loader-wrap"><div className="loader"></div></div> :
          wallets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><Wallet size={40} /></div>
              <h3>No wallets yet</h3>
              <p style={{ fontSize: '0.85rem', marginTop: 6 }}>Click "Add Wallet" to add your first UPI / Bank / USDT receiving address.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="tp-table">
                <thead>
                  <tr><th>QR</th><th>Label</th><th>Type</th><th>Details</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {wallets.map(w => {
                    const meta = TYPE_META[w.type] || TYPE_META.other;
                    return (
                      <tr key={w._id}>
                        <td>
                          {w.qrImageUrl ? (
                            <img src={w.qrImageUrl} alt="QR" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(var(--primary-rgb),0.2)' }} />
                          ) : (
                            <div style={{ width: 40, height: 40, borderRadius: 6, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                              <QrCode size={18} />
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: 600 }}>{w.label}</td>
                        <td>
                          <span className="badge" style={{ background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}30`, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <meta.icon size={12} /> {meta.label}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--text-light)', maxWidth: 260, wordBreak: 'break-all' }}>{renderDetail(w)}</td>
                        <td>
                          <button
                            className={`badge ${w.isActive ? 'badge-success' : 'badge-warning'}`}
                            onClick={() => handleToggle(w)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', border: 'none' }}
                            title="Click to toggle visibility"
                          >
                            {w.isActive ? <><Eye size={12} /> Active</> : <><EyeOff size={12} /> Hidden</>}
                          </button>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-outline btn-sm" onClick={() => openEdit(w)} style={{ fontSize: '0.75rem', padding: '6px 10px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Pencil size={14} /> Edit</span>
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(w)} style={{ fontSize: '0.75rem', padding: '6px 10px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Trash2 size={14} /> Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editing ? 'Edit Wallet' : 'Add Wallet'}</h3>
              <button className="modal-close" onClick={closeModal}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid-2" style={{ gap: 16 }}>
                <div className="form-group">
                  <label>Type *</label>
                  <select name="type" className="form-control" value={form.type} onChange={handleChange}>
                    <option value="upi">UPI</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="usdt">USDT / Crypto</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Label *</label>
                  <input type="text" name="label" className="form-control" placeholder="e.g. Primary UPI" value={form.label} onChange={handleChange} required />
                </div>
              </div>

              {form.type === 'upi' && (
                <div className="form-group">
                  <label>UPI ID *</label>
                  <input type="text" name="upiId" className="form-control" placeholder="trivenipay@upi" value={form.upiId} onChange={handleChange} />
                </div>
              )}

              {form.type === 'bank' && (
                <div className="grid-2" style={{ gap: 16 }}>
                  <div className="form-group">
                    <label>Account Holder Name</label>
                    <input type="text" name="accountHolderName" className="form-control" value={form.accountHolderName} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input type="text" name="bankName" className="form-control" value={form.bankName} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Account Number *</label>
                    <input type="text" name="accountNumber" className="form-control" value={form.accountNumber} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>IFSC Code *</label>
                    <input type="text" name="ifscCode" className="form-control" value={form.ifscCode} onChange={handleChange} />
                  </div>
                </div>
              )}

              {form.type === 'usdt' && (
                <div className="grid-2" style={{ gap: 16 }}>
                  <div className="form-group">
                    <label>Wallet Address *</label>
                    <input type="text" name="usdtAddress" className="form-control" value={form.usdtAddress} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Network</label>
                    <input type="text" name="usdtNetwork" className="form-control" placeholder="TRC20 / ERC20 / BEP20" value={form.usdtNetwork} onChange={handleChange} />
                  </div>
                </div>
              )}

              {form.type === 'other' && (
                <div className="form-group">
                  <label>Details</label>
                  <textarea name="otherDetails" className="form-control" rows={2} value={form.otherDetails} onChange={handleChange} />
                </div>
              )}

              <div className="form-group">
                <label>QR Code Image (optional)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {form.qrImageUrl && (
                    <img src={form.qrImageUrl} alt="QR preview" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(var(--primary-rgb),0.2)' }} />
                  )}
                  <input ref={qrInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleQrFile(e.target.files[0])} />
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => qrInputRef.current?.click()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {form.qrImageUrl ? <><ImageIcon size={14} /> Change QR</> : <><Upload size={14} /> Upload QR</>}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Instructions for users (optional)</label>
                <textarea name="instructions" className="form-control" rows={2} placeholder="e.g. Pay exact amount and upload screenshot immediately" value={form.instructions} onChange={handleChange} />
              </div>

              <div className="grid-2" style={{ gap: 16 }}>
                <div className="form-group">
                  <label>Display Order</label>
                  <input type="number" name="order" className="form-control" value={form.order} onChange={handleChange} />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22 }}>
                  <input type="checkbox" id="walletIsActive" name="isActive" checked={form.isActive} onChange={handleChange} style={{ width: 18, height: 18 }} />
                  <label htmlFor="walletIsActive" style={{ margin: 0, cursor: 'pointer' }}>Visible to users</label>
                </div>
              </div>

              <button type="submit" className="btn btn-primary btn-block" disabled={submitting} style={{ marginTop: 8 }}>
                {submitting ? 'Saving...' : editing ? 'Save Changes' : 'Add Wallet'}
              </button>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}